import type { Ref } from 'vue'

import { computed, ref, watch } from 'vue'

import type {
  OrderingBasketAddonChoice,
  OrderingBasketLine
} from '@/domains/pjazza/types/models/OrderingBasket.types.ts'
import type {
  OrderingMenuAddonGroup,
  OrderingMenuAddonGroupItem,
  OrderingMenuItem
} from '@/domains/pjazza/types/models/OrderingMenu.types.ts'

import { ADDON_GROUP_TYPE } from '@/domains/pjazza/types/consts/AddonGroupType.consts.ts'
import { OPTION_QUANTITY_CEILING } from '@/domains/pjazza/types/consts/Basket.consts.ts'
import { pricedLineTotal } from '@/domains/pjazza/utils/basketLineTotal.ts'

/**
 * Modifier selection for one item, mirroring the server's rules so a diner
 * is told what is wrong before they hit an API error.
 *
 * The server re-runs all of this at quote and placement — this is the
 * courtesy copy, not the enforcement.
 *
 * `existingLine` seeds the selection from a line already in the basket, so
 * reopening a dish with no ambiguous variants edits that line instead of
 * always starting a fresh one at quantity 1.
 *
 * Each chosen option holds a quantity. Null means it is not set yet: an option
 * that follows the item is always at the item's quantity, and an editable one
 * stays at its starting quantity until the diner steps it. Groups count options,
 * never quantities.
 */
export const useItemSelection = (
  item: Ref<OrderingMenuItem | null>,
  addonGroups: Ref<OrderingMenuAddonGroup[]>,
  existingLine: Ref<OrderingBasketLine | null>
) => {
  const quantity = ref(existingLine.value?.quantity ?? 1)
  const notes = ref(existingLine.value?.notes ?? '')
  const seedAddons = (): OrderingBasketAddonChoice[] =>
    existingLine.value?.addons.map(addon => ({ id: addon.id, quantity: addon.quantity })) ?? []

  const selectedAddons = ref<OrderingBasketAddonChoice[]>(seedAddons())
  const hasAttemptedAdd = ref(false)

  // A different item means a clean slate unless it already has a line —
  // keeping a previous item's options would silently carry them onto the
  // new line.
  watch(
    () => item.value?.id,
    () => {
      quantity.value = existingLine.value?.quantity ?? 1
      notes.value = existingLine.value?.notes ?? ''
      selectedAddons.value = seedAddons()
      hasAttemptedAdd.value = false
    }
  )

  const isSelected = (addonId: string): boolean => selectedAddons.value.some(addon => addon.id === addonId)

  const toggleAddon = (group: OrderingMenuAddonGroup, addonId: string): void => {
    const groupIds = group.items.map(addon => addon.id)
    const chosen: OrderingBasketAddonChoice = { id: addonId, quantity: null }

    if (group.type === ADDON_GROUP_TYPE.SINGLE) {
      // Radio behaviour: tapping the chosen option again clears it, unless
      // the group is required.
      const alreadyChosen = isSelected(addonId)
      const others = selectedAddons.value.filter(addon => !groupIds.includes(addon.id))

      selectedAddons.value = alreadyChosen && !(group.min && group.min > 0) ? others : [...others, chosen]

      return
    }

    selectedAddons.value = isSelected(addonId)
      ? selectedAddons.value.filter(addon => addon.id !== addonId)
      : [...selectedAddons.value, chosen]
  }

  /** The ceiling an editable option can step up to. */
  const maxQuantityFor = (addon: OrderingMenuAddonGroupItem): number => addon.maxQuantity ?? OPTION_QUANTITY_CEILING

  /** Where an option starts. The server never checks this; it checks only the allowed range. */
  const startingQuantityFor = (addon: OrderingMenuAddonGroupItem): number => {
    if (!addon.isQuantityEditable) return quantity.value
    if (!addon.isDefaultedToItemQuantity) return 1

    return Math.min(quantity.value, maxQuantityFor(addon))
  }

  /** The option's total on the line, or 0 when it is not chosen. */
  const quantityFor = (addon: OrderingMenuAddonGroupItem): number => {
    const chosen = selectedAddons.value.find(candidate => candidate.id === addon.id)

    if (!chosen) return 0
    if (!addon.isQuantityEditable) return quantity.value

    return chosen.quantity ?? startingQuantityFor(addon)
  }

  /** Steps an editable option. Lowering it to 0 removes it, and the group rules then decide. */
  const setAddonQuantity = (addon: OrderingMenuAddonGroupItem, value: number): void => {
    if (!addon.isQuantityEditable) return

    const next = Math.min(value, maxQuantityFor(addon))

    selectedAddons.value =
      next < 1
        ? selectedAddons.value.filter(candidate => candidate.id !== addon.id)
        : selectedAddons.value.map(candidate =>
            candidate.id === addon.id ? { ...candidate, quantity: next } : candidate
          )
  }

  const countFor = (group: OrderingMenuAddonGroup): number => group.items.filter(addon => isSelected(addon.id)).length

  /** Blocks adding a further option once a group is at its ceiling. */
  const isGroupFull = (group: OrderingMenuAddonGroup): boolean => group.max !== null && countFor(group) >= group.max

  const groupError = (group: OrderingMenuAddonGroup): string | null => {
    const count = countFor(group)

    if (group.min !== null && group.min > 0 && count < group.min) {
      return group.min === 1 ? 'Choose an option' : `Choose at least ${group.min}`
    }

    if (group.max !== null && count > group.max) {
      return `Choose no more than ${group.max}`
    }

    return null
  }

  const errors = computed(() => {
    if (!item.value) return new Map<string, string>()

    return new Map(
      addonGroups.value
        .map(group => [group.id, groupError(group)] as const)
        .filter((entry): entry is [string, string] => entry[1] !== null)
    )
  })

  const isValid = computed(() => errors.value.size === 0)

  /**
   * The first group standing between the diner and the cart, named. The sheet
   * flags every group inline; a toast can only carry one line, so it speaks for
   * the first one in menu order — the one they will reach scrolling up.
   */
  const firstError = computed<{ group: string; message: string } | null>(() => {
    if (!item.value) return null

    const group = addonGroups.value.find(candidate => errors.value.has(candidate.id))
    const message = group ? errors.value.get(group.id) : undefined

    return group && message ? { group: group.name, message } : null
  })

  /** Errors stay hidden until the diner actually tries to add. */
  const visibleErrors = computed(() => (hasAttemptedAdd.value ? errors.value : new Map<string, string>()))

  const chosenItems = computed(() =>
    addonGroups.value.flatMap(group => group.items).filter(addon => isSelected(addon.id))
  )

  /** An option the line's quantity drives: one that follows the item, or one that starts there and was never stepped. */
  const followsLine = (addon: OrderingMenuAddonGroupItem, chosen: OrderingBasketAddonChoice): boolean =>
    !addon.isQuantityEditable || (addon.isDefaultedToItemQuantity && chosen.quantity === null)

  /**
   * The selection as the basket stores it. An option the diner counted holds its
   * count; one the line drives holds null, so it tracks the line's quantity later.
   */
  const choices = computed<OrderingBasketAddonChoice[]>(() =>
    selectedAddons.value.map(chosen => {
      const addon = chosenItems.value.find(candidate => candidate.id === chosen.id)

      if (!addon || followsLine(addon, chosen)) return { id: chosen.id, quantity: null }

      return { id: chosen.id, quantity: quantityFor(addon) }
    })
  )

  const total = computed(() => {
    if (!item.value) return 0

    return pricedLineTotal(
      { price: item.value.price, quantity: quantity.value },
      chosenItems.value.map(addon => ({ price: addon.price, quantity: quantityFor(addon) }))
    )
  })

  const markAttempted = (): void => {
    hasAttemptedAdd.value = true
  }

  /** A setter so the sheet body never writes through its own props. */
  const setNotes = (value: string): void => {
    notes.value = value
  }

  return {
    quantity,
    notes,
    choices,
    isSelected,
    toggleAddon,
    quantityFor,
    maxQuantityFor,
    setAddonQuantity,
    countFor,
    isGroupFull,
    visibleErrors,
    firstError,
    isValid,
    total,
    markAttempted,
    setNotes
  }
}
